import React, { useState, useMemo } from "react"
import { Link, useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import { Helmet } from "react-helmet"
import { slugify } from "../../../utils/slugify"
import {
  getRouteFacts,
  getSiblingRoutes,
  getInboundRoutes,
  isTier1Route,
  titleCase,
} from "../../../utils/routeData"
import { routePath } from "../../../utils/urls"
import Hatchback from "../../../assets/cars/hatchback.webp"
import Sedan from "../../../assets/cars/sedan.webp"
import SUV from "../../../assets/cars/suv.webp"
import demo from "../../../assets/cities/temp.webp"
import "./cityPairView.scss"

const VEHICLE_IMAGE_MAP = { Hatchback, Sedan, SUV }

const CityPairView = ({
  fromCity,
  toCity,
  fromCityName,
  toCityName,
  fromCityData,
  toCityData,
  loading,
}) => {
  const navigate = useNavigate()
  const { distance } = useSelector((state) => state.cab)
  const [openFaqIndex, setOpenFaqIndex] = useState(null)

  // Related routes come from the Tier-1 set, so every link points at a page that
  // exists as prerendered HTML. The previous source (getPopularCitiesInState)
  // returned census place names in the same state, which on a Delhi route meant
  // "Delhi to NewDelhi" and "Delhi to BhalswaJahangirPur" - 63 links per page
  // aimed at intra-city non-routes and cities with no CMS content.
  const siblingRoutes = useMemo(
    () => getSiblingRoutes(fromCity, toCity, 12),
    [fromCity, toCity]
  )
  const inboundRoutes = useMemo(
    () => getInboundRoutes(toCity, fromCity, 12),
    [fromCity, toCity]
  )
  const hasReverse = useMemo(() => isTier1Route(toCity, fromCity), [fromCity, toCity])

  // Real distance/duration/price for this pair, harvested from the CMS at build
  // time. Null for pairs the CMS does not document - in which case we say
  // nothing rather than printing "Calculated at booking".
  const facts = useMemo(() => getRouteFacts(fromCity, toCity), [fromCity, toCity])
  // Prefer the live booking distance when a session has one; otherwise the CMS
  // figure. Either way it is a real number or nothing at all.
  const routeDistanceKm = distance ? Math.ceil(distance) : facts?.distanceKm || null

  // Extract data with fallbacks
  const fromSections = fromCityData?.sections || {}
  const toSections = toCityData?.sections || {}

  // Hero section data
  const heroImage = fromCityData?.hero?.imageUrl || toCityData?.hero?.imageUrl || demo
  const heroTitle = `${fromCityName} to ${toCityName} Cab Service`

  // Intro section - combine from both cities
  const introHeading = `Why Book ${fromCityName} to ${toCityName} Cab with Pulpit?`
  const introParagraphs = useMemo(() => {
    const fromIntro = fromSections.intro?.paragraphs?.[0] || ""
    const toIntro = toSections.intro?.paragraphs?.[0] || ""
    return [
      `${fromCityName} to ${toCityName} is a popular outstation route. When you book a one-way cab from ${fromCityName} to ${toCityName}, you expect punctual service, clean vehicles, and drivers familiar with the route. Our ${fromCityName} to ${toCityName} cab service focuses on reliability, easy booking, and transparent pricing.`,
      fromIntro || `Whether you need a one-way cab from ${fromCityName} to ${toCityName} for business travel, family trips, or special occasions, we aim to keep every journey comfortable and safe.`,
      toIntro || `Planning a trip from ${fromCityName} to ${toCityName}? Book your one-way outstation cab now with transparent pricing and verified drivers.`,
    ]
  }, [fromCityName, toCityName, fromSections, toSections])

  // Taxi rates
  const taxiRates = fromSections.taxiRates?.rates || []

  // Local areas
  const toAreas = toSections.localAreas?.areas || []
  const toInfoCards = toSections.localAreas?.infoCards || []

  // Travel ideas describe the DESTINATION. The heading already said "Things to
  // do in {toCityName}" while the cards came from the origin, so every Delhi
  // route listed Delhi's sights under an Agra heading. Destination content is
  // also what makes these pages differ from one another.
  const travelIdeas = toSections.travelIdeas?.cards || []

  // FAQ items
  const faqItems = useMemo(() => {
    const toFaqs = toSections.faq?.items || []
    const routeSpecificFaqs = [
      {
        q: `How do I book a cab from ${fromCityName} to ${toCityName}?`,
        a: `You can book a one-way outstation cab from ${fromCityName} to ${toCityName} by selecting your pickup and drop locations, date, and time on our booking page. Complete the booking with transparent pricing and verified drivers.`,
      },
      {
        q: `Is one-way cab service available from ${fromCityName} to ${toCityName}?`,
        a: `Yes, one-way cab service is available from ${fromCityName} to ${toCityName}. You only pay for the one-way journey with transparent pricing.`,
      },
      {
        q: `What is the distance and estimated time from ${fromCityName} to ${toCityName}?`,
        // A FAQ answer of "it will be calculated at booking" answers nothing and
        // is poor material for a rich result. Give the real figures when we have
        // them, and otherwise answer the part we can actually speak to.
        a: routeDistanceKm
          ? `${fromCityName} to ${toCityName} is about ${routeDistanceKm} km by road${
              facts?.duration ? `, and the drive usually takes around ${facts.duration}` : ""
            }. Actual time varies with traffic and road conditions.`
          : `Our drivers take the fastest available road route between ${fromCityName} and ${toCityName}. The exact distance and duration for your pickup and drop points are confirmed with your fare before you book.`,
      },
      {
        q: `Are tolls and parking included in the fare for ${fromCityName} to ${toCityName} trip?`,
        a: `Tolls, parking, and state taxes may be extra depending on the route and package. Your final fare confirmation will include all charges clearly mentioned.`,
      },
    ]
    // The origin city's own FAQs are deliberately left out: they are identical
    // on every route leaving that city, which is exactly the duplication these
    // pages suffered from. Route questions plus the destination's are enough.
    const allFaqs = [...routeSpecificFaqs]
    toFaqs.forEach((faq) => {
      if (!allFaqs.some((f) => f.q === faq.q)) {
        allFaqs.push(faq)
      }
    })
    return allFaqs.slice(0, 8)
  }, [fromCityName, toCityName, routeDistanceKm, facts, fromSections, toSections])

  // JSON-LD Schema
  const jsonLdSchema = useMemo(() => {
    const baseUrl = typeof window !== "undefined" ? window.location.origin : "https://pulpitcabs.com"
    return {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: `${baseUrl}/`,
            },
            {
              "@type": "ListItem",
              position: 2,
              name: `${fromCityName} Cab Service`,
              item: `${baseUrl}/cities/${slugify(fromCity)}/`,
            },
            {
              "@type": "ListItem",
              position: 3,
              name: `${fromCityName} to ${toCityName} Cab`,
              item: `${baseUrl}/routes/${slugify(fromCity)}-to-${slugify(toCity)}-cab/`,
            },
          ],
        },
        {
          "@type": "FAQPage",
          mainEntity: faqItems.map((faq) => ({
            "@type": "Question",
            name: faq.q,
            acceptedAnswer: {
              "@type": "Answer",
              text: faq.a,
            },
          })),
        },
        {
          "@type": "Service",
          serviceType: "Outstation Cab Service",
          areaServed: [
            {
              "@type": "City",
              name: fromCityName,
            },
            {
              "@type": "City",
              name: toCityName,
            },
          ],

          name: heroTitle,
          provider: {
            "@type": "Organization",
            name: "Pulpit Cab",
          },
        },
      ],
    }
  }, [fromCity, toCity, fromCityName, toCityName, faqItems, heroTitle])

  if (loading) {
    return (
      <div className="city-view">
        <div className="city-view__container">
          <div style={{ padding: "4rem", textAlign: "center" }}>Loading route details...</div>
        </div>
      </div>
    )
  }

  return (
    <>
      <Helmet defer={false}>
        <script type="application/ld+json">{JSON.stringify(jsonLdSchema)}</script>
      </Helmet>
      <div className="city-view pt-32">
        {/* Hero Image Section */}
        <div className="city-view__hero relative">
          <img
            src={heroImage}
            alt={`${fromCityName} to ${toCityName} cab route`}
            className="city-view__hero-image w-full"
          fetchpriority="high"
          decoding="async"
          />

          <div className="absolute inset-0 flex justify-center items-start bg-black/40 pt-10 md:pt-16">
            <h1 className="text-white text-4xl md:text-6xl font-bold text-center">
              {heroTitle}
            </h1>
          </div>
        </div>


        <div className="city-view__container">
          {/* Intro */}
          <section className="city-view__section">
            <h2 className="city-view__heading">{introHeading}</h2>
            {introParagraphs.map((p, idx) => (
              <p key={idx} className="city-view__paragraph">
                {p}
              </p>
            ))}
          </section>

          {/* Taxi Rates */}
          {taxiRates.length > 0 && (
            <section className="city-view__section city-view__section--highlighted">
              <h2 className="city-view__heading">
                Cab options and fares for {fromCityName} to {toCityName}
              </h2>
              <p className="city-view__paragraph">
                Per-kilometre rates for this route. Choose Hatchback, Sedan, or SUV
                based on group size, luggage, and comfort.
              </p>

              <div className="city-view__rates-grid">
                {taxiRates.map((rate, index) => (
                  <div
                    key={index}
                    className={`city-view__rate-card ${rate.highlight ? "city-view__rate-card--highlight" : ""
                      }`}
                  >
                    {rate.highlight && (
                      <span className="city-view__rate-badge-highlight">
                        {rate.highlightText || "Popular Choice"}
                      </span>
                    )}

                    <div className="h-44 flex items-center justify-center">
                      <img
                        src={VEHICLE_IMAGE_MAP[rate.type] || Sedan}
                        alt={rate.imageAlt || `${rate.type} cab`}
                        className="h-32 object-contain"
                      />
                    </div>

                    <h3 className="city-view__rate-type">{rate.type}</h3>
                    <p className="city-view__rate-model">{rate.desc}</p>
                    <div className="city-view__rate-badge">{rate.seats} seats</div>
                    <div className="city-view__rate-price">{rate.rate}</div>
                    <p className="city-view__rate-best">{rate.bestFor}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Drop Areas in ToCity */}
          {toAreas.length > 0 && (
            <section className="city-view__section">
              <h2 className="city-view__heading">
                {toSections.localAreas?.heading || `Drop Areas in ${toCityName}`}
              </h2>
              <p className="city-view__paragraph">{toSections.localAreas?.subText}</p>

              <div className="city-view__local-areas">
                {toAreas.map((area, index) => (
                  <span key={index} className="city-view__area-tag">
                    {area}
                  </span>
                ))}
              </div>

              {toInfoCards.length > 0 && (
                <div className="city-view__local-info">
                  {toInfoCards.map((card, idx) => (
                    <div key={idx} className="city-view__info-card">
                      <h3 className="city-view__info-title">{card.title}</h3>
                      <p className="city-view__info-text">{card.text}</p>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Travel Ideas */}
          {travelIdeas.length > 0 && (
            <section className="city-view__section">
              <h2 className="city-view__heading">
                {toSections.travelIdeas?.heading || `Things to do in ${toCityName} with a cab`}
              </h2>
              <p className="city-view__paragraph">{toSections.travelIdeas?.subText}</p>

              <div className="city-view__travel-grid">
                {travelIdeas.map((card, idx) => (
                  <div key={idx} className="city-view__travel-card">
                    <h3 className="city-view__travel-title">{card.title}</h3>
                    <p className="city-view__travel-text">{card.text}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* Route Guide / Distance & Time */}
          <section className="city-view__section city-view__section--highlighted">
            <h2 className="city-view__heading">Route Guide: {fromCityName} to {toCityName}</h2>
            {/* Only state a distance we actually have. The live `distance` comes
                from a booking session and is absent for a cold visitor; `facts`
                is the CMS figure baked in at build time. */}
            {(routeDistanceKm || facts?.duration || facts?.startingPrice) && (
              <p className="city-view__paragraph">
                {routeDistanceKm && (
                  <>
                    <strong>Distance:</strong> about {routeDistanceKm} km by road.{" "}
                  </>
                )}
                {facts?.duration && (
                  <>
                    <strong>Travel time:</strong> around {facts.duration}.{" "}
                  </>
                )}
                {facts?.startingPrice && (
                  <>
                    <strong>Fares from:</strong> {facts.startingPrice} for a one-way trip.
                  </>
                )}
              </p>
            )}
            <p className="city-view__paragraph">
              <strong>Best Time to Travel:</strong> Early mornings (6 AM - 9 AM) and late evenings (6 PM - 9 PM) are
              ideal for avoiding traffic. Weekend travel may have higher demand.
            </p>
            <p className="city-view__paragraph">
              <strong>Recommended Stops:</strong> Consider a short break at a midpoint location for refreshments and to
              stretch your legs during the journey.
            </p>
            {hasReverse && (
              <p className="city-view__paragraph">
                <strong>Returning?</strong>{" "}
                <Link to={routePath(toCity, fromCity)} className="city-view__route-link">
                  Book a cab from {toCityName} to {fromCityName}
                </Link>{" "}
                for the journey back.
              </p>
            )}
          </section>

          {/* FAQ */}
          {faqItems.length > 0 && (
            <section className="city-view__section">
              <h2 className="city-view__heading">
                FAQs for {fromCityName} to {toCityName} cab booking
              </h2>

              <div className="city-view__faq-grid">
                {faqItems.map((faq, index) => (
                  <div key={index} className="city-view__faq-card">
                    <h3 className="city-view__faq-question">{faq.q}</h3>
                    <p className="city-view__faq-answer">{faq.a}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* CTA */}
          <section className="city-view__section city-view__section--cta">
            <div className="city-view__cta-box">
              <h2 className="city-view__cta-title">
                Ready to book your {fromCityName} to {toCityName} cab?
              </h2>
              <p className="city-view__cta-text">
                For a reliable one-way outstation cab from {fromCityName} to {toCityName},
                book online or reach us on WhatsApp for help.
              </p>

              <div className="city-view__cta-buttons">
                <button
                  className="city-view__cta-button city-view__cta-button--primary"
                  onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                >
                  Select Cab
                </button>
              </div>

            </div>
          </section>

          {/* Related routes.
              Restricted to the Tier-1 set so every link lands on a page that
              exists as prerendered HTML with real content. The blocks these
              replaced were generated from same-state census names and produced
              links like "Delhi to NewDelhi Cab" and "Delhi Airport to
              BhalswaJahangirPur Cab". */}
          {siblingRoutes.length > 0 && (
            <section className="city-view__section">
              <div className="bg-white rounded-[10px] shadow-sm px-[12px] py-[12px] md:px-[44px] md:py-[32px] border">
                <h2 className="text-[16px] font-black text-[#1B1B1B] mb-6">
                  Other cab routes from {fromCityName}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {siblingRoutes.map((city) => {
                    const routeText = `${fromCityName} to ${titleCase(city)} Cab`
                    return (
                      <Link
                        key={city}
                        to={routePath(fromCity, city)}
                        title={routeText}
                        className="block px-3 py-2 text-[15px] font-medium text-[#1B1B1B] bg-gray-50 hover:bg-[#2DB8B3] hover:text-white rounded-md border border-gray-200 hover:border-[#2DB8B3] transition-all duration-200 truncate"
                      >
                        {routeText}
                      </Link>
                    )
                  })}
                </div>
              </div>
            </section>
          )}

          {inboundRoutes.length > 0 && (
            <section className="city-view__section">
              <div className="bg-white rounded-[10px] shadow-sm px-[12px] py-[12px] md:px-[44px] md:py-[32px] border">
                <h2 className="text-[16px] font-black text-[#1B1B1B] mb-6">
                  Cab routes to {toCityName} from other cities
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                  {inboundRoutes.map((city) => {
                    const routeText = `${titleCase(city)} to ${toCityName} Cab`
                    return (
                      <Link
                        key={city}
                        to={routePath(city, toCity)}
                        title={routeText}
                        className="block px-3 py-2 text-[15px] font-medium text-[#1B1B1B] bg-gray-50 hover:bg-[#2DB8B3] hover:text-white rounded-md border border-gray-200 hover:border-[#2DB8B3] transition-all duration-200 truncate"
                      >
                        {routeText}
                      </Link>
                    )
                  })}
                </div>
              </div>
            </section>
          )}

        </div>
      </div>
    </>
  )
}

export default CityPairView
